import { Link } from 'react-router-dom';
import { Phone, Mail, Calendar, Clock, Sparkles, CheckCircle2, ArrowRight, Truck } from 'lucide-react';
import SEO from '../components/SEO';

export default function FoodtruckCatering() {
  const bookingSteps = [
    {
      step: "01",
      title: "Kontakt & Dato",
      description: "Ring på 52 90 13 06 eller send en mail med din ønskede dato og tidspunkt for dit arrangement."
    },
    {
      step: "02",
      title: "Sammensæt Menuen",
      description: "Sammen med dig sammensætter vi den helt rigtige menu, om I er til rød karry, sprøde forårsruller, stegte nudler eller friske salater."
    },
    {
      step: "03",
      title: "Nyd Festen",
      description: "Vi ankommer med foodtrucken, tilbereder maden frisk på stedet, og serverer den rygende varm til dine gæster."
    }
  ];

  return (
    <>
      <SEO
        title="Foodtruck i Skibby | Bestil foodtruck med thai mad"
        description="Skal du holde fest hjælper vi dig gerne med maden. Vi tilbyder tilpassede catering menuer med lækre friske råvarer lavet som det smager i Thailand. Bestil en foodtruck i dag!"
      />

      {/* Hero Banner */}
      <section className="relative pt-56 pb-20 bg-brand-dark text-white text-left">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-foodtruck-premium.jpg"
            alt="Thai Foodtruck Event"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark via-brand-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Foodtruck & Catering
          </h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="relative py-20 bg-white text-brand-dark overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute right-[-5%] bottom-[-5%] text-gray-100 pointer-events-none select-none z-0 hidden lg:block">
          <Truck className="w-[400px] h-[400px] opacity-[0.05]" strokeWidth={0.5} />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Left Column: Verbatim Text & Description */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark">
                Skal du holde fest hjælper vi dig gerne med maden
              </h2>
              <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                Vi tilbyder tilpassede catering menuer med lækre friske råvarer lavet som det smager i Thailand. Læn dig blot tilbage og drøm dig væk til varme himmelstrøg og lad os levere maden til din næste fest. Sammen med dig kan vi sammensætte den helt rigtige menu, om du og dine gæster er til sprøde forårsruller, rød- eller grønkarry, stegte nudler eller friske salater. Vi kan også levere vegetariske retter eller lave de forskellige retter med lige den kødpræference, som du ønsker.
              </p>
              <p className="text-gray-700 leading-relaxed text-base sm:text-lg font-medium border-l-4 border-brand-primary pl-4 py-1">
                Ønsker du et ekstra pift til festen kommer vi også gerne med foodtrucken, som helt sikkert kan gøre din fest uforglemmelig.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Kontakt os gerne for at tale om dine muligheder og ønsker – vi glæder os til at høre fra dig.
              </p>

              {/* Direct Contact Info */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-4 border-t border-gray-200 pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center text-black shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Ring direkte til Foodtruck</span>
                    <a href="tel:+4552901306" className="text-lg font-bold text-brand-dark hover:text-brand-primary transition-colors">
                      52 90 13 06
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center text-black shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Send os en e-mail</span>
                    <a href="mailto:jes.valente@gmail.com" className="text-lg font-bold text-brand-dark hover:text-brand-primary transition-colors">
                      jes.valente@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Booking Process */}
            <div className="lg:col-span-5 bg-white p-8 rounded-3xl border border-gray-100 shadow-lg flex flex-col gap-6">
              <h3 className="font-display text-xl font-bold text-brand-dark flex items-center gap-2">
                <Calendar className="w-5 h-5 text-brand-primary" />
                Sådan booker du
              </h3>
              <p className="text-xs text-gray-500">
                Vi har gjort det nemt og overskueligt at få leveret mad eller booke vores foodtruck til dit arrangement.
              </p>

              <div className="flex flex-col gap-6">
                {bookingSteps.map((step, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="font-display text-2xl font-bold text-brand-primary/20 shrink-0 mt-1">
                      {step.step}
                    </div>
                    <div className="flex flex-col gap-1">
                      <h4 className="font-semibold text-sm text-brand-dark">{step.title}</h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-3.5 rounded-full transition-all shadow-md mt-4"
              >
                Gå til bookingformular
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
